import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIonAlert, useIonToast } from "@ionic/react";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { ProfileContext } from "../../context/ProfileContext";
import { CrewContext } from "../../context/CrewContext";
import { db } from "../../firebase";
import { FB_COLLECTION_PATH } from "../../functions/postActionTypes";
import { saveAnnouncement } from "../../domain/announcements/announcementActions";

interface AnnouncementEditorPanelProps {
  mode: "create" | "edit";
  announcementId?: string | null;
  onCancel: () => void;
  onSaved: (announcementId: string) => void;
}

export const AnnouncementEditorPanel: React.FC<AnnouncementEditorPanelProps> = ({
  mode,
  announcementId,
  onCancel,
  onSaved,
}) => {
  const { profile, myAssociates, myAnnouncementPosts } = useContext(ProfileContext);
  const { crews, crewsLoading } = useContext(CrewContext);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [recipientUids, setRecipientUids] = useState<string[]>([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState<string[]>([]);

  const [isAudiencePanelOpen, setIsAudiencePanelOpen] = useState(false);
  const [audienceSearch, setAudienceSearch] = useState("");
  const [isCrewListOpen, setIsCrewListOpen] = useState(false);

  const storage = getStorage();

  useEffect(() => {
    if (mode !== "edit" || !announcementId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getDoc(doc(db, FB_COLLECTION_PATH.Announcements, announcementId))
      .then((snap) => {
        if (!snap.exists()) return;

        const data: any = snap.data() || {};
        setTitle(String(data?.title || ""));
        setBody(String(data?.body || ""));
        setImageUrl(String(data?.imageUrl || ""));
        setIsPinned(Boolean(data?.isPinned));
        setRecipientUids(Array.isArray(data?.recipientUids) ? data.recipientUids.filter(Boolean) : []);
        setSelectedCrewIds(Array.isArray(data?.selectedCrewIds) ? data.selectedCrewIds.filter(Boolean) : []);
      })
      .finally(() => setLoading(false));
  }, [mode, announcementId]);

  const selectedCount = recipientUids.length;

  const audiencePreview = useMemo(() => {
    if (recipientUids.length === 0) return "No audience selected";

    const names = recipientUids
      .map((uid) => {
        const assoc = (myAssociates || []).find((a: any) => String(a?.WerkerUid || "") === uid);
        if (!assoc) return "Unknown User";
        return `${assoc?.WerkerFirstName || ""} ${assoc?.WerkerLastName || ""}`.trim();
      })
      .filter(Boolean);

    if (names.length <= 3) return names.join(", ");
    return `${names.slice(0, 3).join(", ")} +${names.length - 3} more`;
  }, [recipientUids, myAssociates]);

  const filteredAssociates = useMemo(() => {
    const query = (audienceSearch || "").trim().toLowerCase();
    const base = Array.isArray(myAssociates) ? myAssociates : [];

    return base
      .filter((assoc: any) => {
        const uid = String(assoc?.WerkerUid || "");
        if (!uid) return false;
        if (!query) return true;

        const first = String(assoc?.WerkerFirstName || "").toLowerCase();
        const last = String(assoc?.WerkerLastName || "").toLowerCase();
        const company = String(assoc?.WerkerCompany || "").toLowerCase();
        const occupation = String(assoc?.Occupation || "").toLowerCase();

        return first.includes(query) || last.includes(query) || company.includes(query) || occupation.includes(query);
      })
      .sort((a: any, b: any) => String(a?.WerkerFirstName || "").localeCompare(String(b?.WerkerFirstName || "")));
  }, [myAssociates, audienceSearch]);

  const toggleRecipient = (uid: string) => {
    setRecipientUids((prev) => (prev.includes(uid) ? prev.filter((v) => v !== uid) : [...prev, uid]));
  };

  const allFilteredSelected = useMemo(() => {
    if (filteredAssociates.length === 0) return false;
    const selected = new Set(recipientUids);
    return filteredAssociates.every((assoc: any) => selected.has(String(assoc?.WerkerUid || "")));
  }, [filteredAssociates, recipientUids]);

  const visibleSelectedCount = useMemo(() => {
    const selected = new Set(recipientUids);
    return filteredAssociates.filter((assoc: any) => selected.has(String(assoc?.WerkerUid || ""))).length;
  }, [filteredAssociates, recipientUids]);

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredAssociates
      .map((assoc: any) => String(assoc?.WerkerUid || ""))
      .filter(Boolean);

    if (filteredIds.length === 0) return;

    if (allFilteredSelected) {
      const set = new Set(filteredIds);
      setRecipientUids((prev) => prev.filter((id) => !set.has(id)));
      return;
    }

    setRecipientUids((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const getInitials = (name: string) => {
    const parts = String(name || "").trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "C";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const applyCrewToSelection = async (crew: any) => {
    const rawIds = Array.isArray(crew?.memberIds) ? crew.memberIds : [];
    const associateIdSet = new Set(
      (Array.isArray(myAssociates) ? myAssociates : [])
        .map((a: any) => String(a?.WerkerUid || ""))
        .filter(Boolean)
    );

    const validCrewIds = rawIds
      .filter((id: any) => typeof id === "string" && id.length > 0)
      .filter((id: string) => associateIdSet.has(id));

    if (validCrewIds.length === 0) {
      await presentAlert({
        header: "Crew is Empty",
        message: `"${crew?.name || "Crew"}" has no selectable associates.`,
        buttons: ["OK"],
      });
      return;
    }

    setRecipientUids((prev) => Array.from(new Set([...prev, ...validCrewIds])));
    if (crew?.id) {
      setSelectedCrewIds((prev) => (prev.includes(crew.id) ? prev : [...prev, crew.id]));
    }

    await presentToast({
      message: `Added ${validCrewIds.length} from ${crew?.name || "crew"}`,
      duration: 1300,
      position: "top",
    });
  };

  const validate = async () => {
    if (!profile?.UserId) {
      await presentAlert({
        header: "Unable to Continue",
        message: "Your profile is not ready yet.",
        buttons: ["OK"],
      });
      return false;
    }

    if (!title.trim() && !body.trim()) {
      await presentAlert({
        header: "Missing Content",
        message: "Please enter a title or a message for this announcement.",
        buttons: ["OK"],
      });
      return false;
    }

    if (recipientUids.length === 0) {
      await presentAlert({
        header: "No Audience Selected",
        message: "Please choose at least one associate for this announcement.",
        buttons: ["OK"],
      });
      return false;
    }

    return true;
  };

  const handlePickPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        await presentAlert({
          header: "Image Too Large",
          message: "Please choose an image under 5MB.",
          buttons: ["OK"],
        });
        return;
      }

      setUploadingPhoto(true);
      const filePath = `announcementImages/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setImageUrl(downloadURL);

      await presentToast({ message: "Photo added", duration: 1200, position: "top", color: "medium" });
    } catch (error) {
      // console.log("desktop pick announcement photo error:", error);
      await presentAlert({
        header: "Photo Error",
        message: "Unable to add photo right now.",
        buttons: ["OK"],
      });
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = async () => {
    setImageUrl("");
    await presentToast({ message: "Photo removed", duration: 1100, position: "top", color: "medium" });
  };

  const handleSave = async () => {
    const valid = await validate();
    if (!valid) return;

    setSaving(true);

    try {
      const id = await saveAnnouncement({
        mode,
        announcementId: mode === "edit" ? String(announcementId || "") : undefined,
        profile,
        title,
        body,
        imageUrl,
        isPinned,
        recipientUids,
        selectedCrewIds,
        myAnnouncementPosts,
      });

      await presentToast({
        message: mode === "edit" ? "Announcement updated" : "Announcement sent",
        duration: 1300,
        position: "top",
        color: "medium",
      });

      onSaved(id);
    } catch (error) {
      // console.log("desktop announcement save error:", error);
      await presentAlert({
        header: "Error",
        message:
          mode === "edit"
            ? "Something went wrong while updating the announcement."
            : "Something went wrong while sending the announcement.",
        buttons: ["OK"],
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
        <div
          style={{
            background: "var(--ion-item-background, var(--ion-background-color))",
            borderRadius: 14,
            padding: 16,
            border: "1px solid rgba(0,0,0,0.08)",
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 10 }}>
            {mode === "edit" ? "Edit Announcement" : "Create Announcement"}
          </div>

          <label style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            <span style={{ fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value || "")}
              placeholder="Enter title (optional)"
              maxLength={120}
              style={{ padding: "10px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.10)" }}
            />
          </label>

          <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: "0.86rem", fontWeight: 600, color: "var(--ion-color-medium)" }}>Message</span>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {!imageUrl && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: "transparent",
                      borderRadius: 8,
                      padding: "6px 10px",
                    }}
                  >
                    {uploadingPhoto ? "Adding..." : "Add Photo"}
                  </button>
                )}

                {!!imageUrl && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    style={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      background: "transparent",
                      borderRadius: 8,
                      padding: "6px 10px",
                    }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            </div>

            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value || "")}
              placeholder="Write your announcement..."
              maxLength={2000}
              rows={4}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                lineHeight: 1.5,
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div style={{ marginTop: 5, fontSize: "0.84rem", color: "var(--ion-color-medium)" }}>{body.trim().length}/2000 characters</div>

            {!!imageUrl && (
              <div style={{ marginTop: 12 }}>
                <img
                  src={imageUrl}
                  alt="Announcement preview"
                  style={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 12, display: "block" }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePickPhoto}
            style={{ display: "none" }}
          />
        </div>

        <div
          style={{
            background: "var(--ion-item-background, var(--ion-background-color))",
            borderRadius: 14,
            padding: 16,
            border: "1px solid rgba(0,0,0,0.08)",
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>Audience</div>
              <div style={{ marginTop: 4, fontSize: "0.9rem", color: "var(--ion-color-medium)" }}>
                {selectedCount > 0 ? `${selectedCount} recipients selected` : "No audience selected"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsAudiencePanelOpen((prev) => !prev)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 84,
                height: 34,
                padding: "0 14px",
                borderRadius: 999,
                border: isAudiencePanelOpen
                  ? "1px solid var(--ion-color-step-300, rgba(255,255,255,0.25))"
                  : "1px solid var(--ion-color-secondwarning, #f6c343)",
                background: isAudiencePanelOpen
                  ? "var(--ion-color-step-50, rgba(255,255,255,0.06))"
                  : "var(--ion-color-secondwarning, #f6c343)",
                color: isAudiencePanelOpen
                  ? "var(--ion-text-color)"
                  : "var(--ion-color-secondwarning-contrast, #111)",
                fontWeight: 700,
                fontSize: "0.84rem",
                letterSpacing: "0.01em",
                boxShadow: isAudiencePanelOpen
                  ? "none"
                  : "0 2px 10px rgba(0,0,0,0.10)",
                cursor: "pointer",
              }}
            >
              {isAudiencePanelOpen ? "Done" : "Select"}
            </button>
          </div>

          {selectedCount > 0 && (
            <div style={{ fontSize: "0.88rem", color: "var(--ion-color-medium)", marginBottom: 8 }}>
              {audiencePreview}
            </div>
          )}
        </div>

        <div
          style={{
            background: "var(--ion-item-background, var(--ion-background-color))",
            borderRadius: 14,
            padding: 16,
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 12 }}>Preview</div>

          <div style={{ background: "var(--ion-background-color)", borderRadius: 14, padding: 14, border: "1px solid rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
              <img
                src={profile?.ProfilePicURL || "../assets/profilePic.png"}
                alt=""
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
              />

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>
                  {`${profile?.FirstName || ""} ${profile?.LastName || ""}`.trim() || "You"}
                </div>
                <div style={{ fontSize: "0.86rem", color: "var(--ion-color-medium)" }}>{profile?.Company || ""}</div>
              </div>

              <div style={{ fontSize: "0.82rem", color: "var(--ion-color-medium)", whiteSpace: "nowrap" }}>Now</div>
            </div>

            <h3 style={{ marginTop: 0, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              {title.trim() || "Untitled"}
            </h3>

            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, fontSize: "0.98rem", marginBottom: imageUrl ? 12 : 0 }}>
              {body.trim() || "Your announcement preview will show here."}
            </div>

            {!!imageUrl && (
              <img
                src={imageUrl}
                alt="Announcement preview"
                style={{ width: "100%", maxHeight: 320, objectFit: "cover", borderRadius: 12 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(0,0,0,0.08)",
          padding: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          background: "var(--ion-background-color)",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            border: "1px solid rgba(0,0,0,0.18)",
            background: "transparent",
            borderRadius: 10,
            padding: "10px 14px",
            minWidth: 100,
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || uploadingPhoto}
          style={{
            border: "none",
            background: "var(--ion-color-secondwarning, #f6c343)",
            color: "#111",
            borderRadius: 10,
            padding: "10px 16px",
            fontWeight: 700,
            minWidth: 180,
          }}
        >
          {saving ? (mode === "edit" ? "Saving..." : "Sending...") : mode === "edit" ? "Save Changes" : "Send Announcement"}
        </button>
      </div>

      {isAudiencePanelOpen && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 30,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(720px, 100%)",
              maxHeight: "min(82vh, 760px)",
              background: "var(--ion-background-color)",
              borderRadius: 14,
              border: "1px solid var(--ion-color-step-200, rgba(255,255,255,0.12))",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid var(--ion-color-step-150, rgba(255,255,255,0.10))",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: "1rem" }}>Select Audience</div>
                <div style={{ marginTop: 2, fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>
                  {selectedCount > 0 ? `${selectedCount} selected` : "No recipients selected"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsAudiencePanelOpen(false)}
                style={{
                  height: 34,
                  padding: "0 14px",
                  borderRadius: 999,
                  border: "1px solid var(--ion-color-step-300, rgba(255,255,255,0.25))",
                  background: "var(--ion-color-step-50, rgba(255,255,255,0.06))",
                  color: "var(--ion-text-color)",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>

            <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--ion-color-step-150, rgba(255,255,255,0.10))" }}>
              <input
                value={audienceSearch}
                onChange={(e) => setAudienceSearch(e.target.value || "")}
                placeholder="Search associates"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid var(--ion-color-step-250, rgba(255,255,255,0.18))",
                  background: "var(--ion-item-background, var(--ion-background-color))",
                  color: "var(--ion-text-color)",
                }}
              />

              <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setIsCrewListOpen((prev) => !prev)}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 999,
                    border: "1px solid var(--ion-color-step-250, rgba(255,255,255,0.18))",
                    background: "var(--ion-color-step-50, rgba(255,255,255,0.06))",
                    color: "var(--ion-text-color)",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {isCrewListOpen ? "Hide Crews" : "Add Crew"}
                </button>

                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  disabled={filteredAssociates.length === 0}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 999,
                    border: "1px solid var(--ion-color-step-250, rgba(255,255,255,0.18))",
                    background: filteredAssociates.length === 0
                      ? "var(--ion-color-step-100, rgba(255,255,255,0.04))"
                      : "var(--ion-color-step-50, rgba(255,255,255,0.06))",
                    color: "var(--ion-text-color)",
                    fontWeight: 700,
                    cursor: filteredAssociates.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {allFilteredSelected ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div style={{ marginTop: 6, fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>
                {visibleSelectedCount} selected / {filteredAssociates.length} shown
              </div>
            </div>

            {isCrewListOpen && (
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid var(--ion-color-step-150, rgba(255,255,255,0.10))",
                  display: "grid",
                  gap: 8,
                  maxHeight: 190,
                  overflowY: "auto",
                }}
              >
                {crewsLoading && (
                  <div style={{ fontSize: "0.88rem", color: "var(--ion-color-medium)" }}>Loading crews...</div>
                )}

                {!crewsLoading && (!crews || crews.length === 0) && (
                  <div style={{ fontSize: "0.88rem", color: "var(--ion-color-medium)" }}>No crews yet.</div>
                )}

                {!crewsLoading && (crews || []).map((crew: any) => (
                  <button
                    key={crew?.id || crew?.name}
                    type="button"
                    onClick={() => applyCrewToSelection(crew)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "30px 1fr auto",
                      gap: 10,
                      alignItems: "center",
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid var(--ion-color-step-200, rgba(255,255,255,0.14))",
                      background: "var(--ion-item-background, var(--ion-background-color))",
                      color: "var(--ion-text-color)",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {crew?.avatarUrl ? (
                      <img
                        src={crew.avatarUrl}
                        alt=""
                        style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: 12,
                          background: "var(--ion-color-step-150, rgba(255,255,255,0.14))",
                        }}
                      >
                        {getInitials(crew?.name || "Crew")}
                      </div>
                    )}

                    <span>
                      <strong>{crew?.name || "Crew"}</strong>
                      <span style={{ display: "block", fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>
                        {crew?.description || "No description"}
                      </span>
                    </span>

                    <span
                      style={{
                        fontSize: "0.78rem",
                        color: "var(--ion-color-medium)",
                        border: "1px solid var(--ion-color-step-250, rgba(255,255,255,0.18))",
                        borderRadius: 999,
                        padding: "2px 8px",
                      }}
                    >
                      {Number(crew?.memberCount || 0)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ padding: 14, overflowY: "auto", display: "grid", gap: 8 }}>
              {filteredAssociates.map((assoc: any) => {
                const uid = String(assoc?.WerkerUid || "");
                const checked = recipientUids.includes(uid);

                return (
                  <label
                    key={uid}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr auto",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 10px",
                      borderRadius: 10,
                      border: checked
                        ? "1px solid var(--ion-color-warning, #f6c343)"
                        : "1px solid var(--ion-color-step-200, rgba(255,255,255,0.14))",
                      background: checked
                        ? "rgba(246,195,67,0.10)"
                        : "var(--ion-item-background, var(--ion-background-color))",
                    }}
                  >
                    <img
                      src={assoc?.WerkerProfilePicURL || "../assets/profilePic.png"}
                      alt=""
                      style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
                    />

                    <span>
                      <strong>{assoc?.WerkerFirstName || ""} {assoc?.WerkerLastName || ""}</strong>
                      <span style={{ display: "block", fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>
                        {assoc?.Occupation || assoc?.WerkerCompany || ""}
                      </span>
                    </span>

                    <input type="checkbox" checked={checked} onChange={() => toggleRecipient(uid)} />
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
